import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import styles from './index.module.scss';

const GROUP_BY_OPTIONS = [
    { value: 'day',       label: 'Day' },
    { value: 'teacher',   label: 'Teacher' },
    { value: 'level',     label: 'Level' },
];

// Match the same API URL resolution used by RequestService
const getApiUrl = () =>
    (window._env_ && window._env_.REACT_APP_API_URL) ||
    process.env.REACT_APP_API_URL ||
    'http://localhost:8080';

// Read the user's current "color by" setting - same key as ScheduleTableTab / WeekView
const COLOR_BY_STORAGE_KEY = 'color_by_storage_key';
const getColorBy = () => localStorage.getItem(COLOR_BY_STORAGE_KEY) || 'levels';

// Builds the one line an owner reads when an export fails: the status and the
// server's own message when there is one, the network error when there is no
// reply at all. The server already works out the reason (no access, no board,
// too many classes to fit); throwing it away left every failure reading the same.
export async function describeExportError(err, label) {
    let msg = `${label} export failed.`;
    if (err && err.response) {
        msg += ` (${err.response.status})`;
        if (err.response.data instanceof Blob) {
            try {
                const json = JSON.parse(await err.response.data.text());
                if (json && json.message) msg += `: ${json.message}`;
            } catch (e) { /* blob was not JSON */ }
        }
    } else if (err && err.message) {
        msg += ` ${err.message}`;
    }
    return msg;
}

export default function ExportPanel({ board, weekday, onPdfExport, userId, isWeekView = false, onPdfFitExport = null }) {
    const [open, setOpen]       = useState(false);
    const [format, setFormat]   = useState('pdf');
    const [groupBy, setGroupBy] = useState('day');
    const [loading, setLoading] = useState(false);
    const [fitToPage, setFitToPage] = useState(false);
    const panelRef              = useRef(null);

    const storedFilters = useSelector((state) => state.scheduleFilters.filters);

    // Derive active filter tags to display
    const activeFilterTags = React.useMemo(() => {
        if (!storedFilters) return [];
        const tags = [];
        Object.entries(storedFilters).forEach(([key, values]) => {
            if (!Array.isArray(values)) return;
            values.filter(v => v?.checked).forEach(v => {
                tags.push({ key, label: v.label || v.value });
            });
        });
        return tags;
    }, [storedFilters]);

    // Build filter query string entries for the List export API call
    const buildFilterEntries = () => {
        if (!storedFilters) return [];
        const entries = [];
        const keyMap = {
            levels:   'filterLevels[]',
            teachers: 'filterTeachers[]',
            styles:   'filterStyles[]',
            length:   'filterLengths[]',
        };
        Object.entries(storedFilters).forEach(([key, values]) => {
            if (!Array.isArray(values) || !keyMap[key]) return;
            values.filter(v => v?.checked).forEach(v => {
                // Length values are stored as float hours (e.g. 0.5, 0.75, 1.0) - send the numeric value
                // All other filters use the label (display name) for string matching on the backend
                const paramVal = key === 'length' ? String(v.value) : (v.label || v.value);
                entries.push([keyMap[key], paramVal]);
            });
        });
        return entries;
    };

    // Close panel when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const handleExportXLS = async () => {
        setLoading(true);
        try {
            const base = getApiUrl().replace(/\/$/, '');
            const params = new URLSearchParams([
                ['userId',  userId],
                ['boardId', board?.id],
                ['colorBy', getColorBy()],
                ...buildFilterEntries(),
            ]);
            const url = `${base}/schedules/export-grid-xls?${params.toString()}`;
            const response = await axios.get(url, {
                responseType: 'blob',
                headers: { 'Authorization': 'Bearer ' + localStorage.getItem('accessToken') },
            });
            const blobUrl = window.URL.createObjectURL(
                new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
            );
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = `${board?.boardName || 'schedule'}-schedule.xlsx`;
            a.click();
            window.URL.revokeObjectURL(blobUrl);
            setOpen(false);
        } catch (err) {
            console.error('[ExportPanel] XLS export failed:', err);
            alert(await describeExportError(err, 'XLS'));
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        if (format === 'xls') {
            await handleExportXLS();
            return;
        }

        if (format === 'pdf') {
            // Server-side PDF grid - no browser print dialog, no DOM clone quirks.
            // Falls back to the legacy react-to-print path only if weekday is unavailable.
            if (!weekday) {
                // Week view - export all active days as a multi-page PDF
                if (!isWeekView) {
                    setOpen(false);
                    if (onPdfExport) onPdfExport();
                    return;
                }
                setLoading(true);
                try {
                    const base = getApiUrl().replace(/\/$/, '');
                    const params = new URLSearchParams([
                        ['userId',  userId],
                        ['boardId', board?.id],
                        ['colorBy', getColorBy()],
                        ['fitToPage', fitToPage ? 'true' : 'false'],
                        ...buildFilterEntries(),
                    ]);
                    const url = `${base}/schedules/export-grid-week?${params.toString()}`;
                    const response = await axios.get(url, {
                        responseType: 'blob',
                        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('accessToken') },
                    });
                    const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    window.open(blobUrl, '_blank');
                    setOpen(false);
                } catch (err) {
                    console.error('[ExportPanel] Week PDF export failed:', err);
                    alert(await describeExportError(err, 'Week PDF'));
                } finally {
                    setLoading(false);
                }
                return;
            }
            setLoading(true);
            try {
                const base = getApiUrl().replace(/\/$/, '');
                const params = new URLSearchParams([
                    ['userId',  userId],
                    ['boardId', board?.id],
                    ['weekday', weekday],
                    ['colorBy', getColorBy()],
                    ...buildFilterEntries(),
                ]);
                const url = `${base}/schedules/export-grid?${params.toString()}`;
                const response = await axios.get(url, {
                    responseType: 'blob',
                    headers: { 'Authorization': 'Bearer ' + localStorage.getItem('accessToken') },
                });
                const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                // Open in a new tab so the user can preview before saving/printing
                window.open(blobUrl, '_blank');
                // Note: we intentionally do NOT revoke the URL immediately - the tab needs it.
                // The browser will clean it up when the tab is closed.
                setOpen(false);
            } catch (err) {
                console.error('[ExportPanel] Grid PDF export failed:', err);
                alert(await describeExportError(err, 'PDF'));
            } finally {
                setLoading(false);
            }
            return;
        }

        if (format === 'list') {
            setLoading(true);
            try {
                // Use full backend URL - same resolution as RequestService
                const base = getApiUrl().replace(/\/$/, '');
                const params = new URLSearchParams([
                    ['userId',  userId],
                    ['boardId', board?.id],
                    ['groupBy', groupBy],
                    ...buildFilterEntries(),
                ]);

                const url = `${base}/schedules/export-list?${params.toString()}`;

                const response = await axios.get(url, {
                    responseType: 'blob',
                    headers: {
                        'Authorization': 'Bearer ' + localStorage.getItem('accessToken'),
                    },
                });

                const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = `${board?.boardName || 'schedule'}-list.pdf`;
                a.click();
                window.URL.revokeObjectURL(blobUrl);
                setOpen(false);
            } catch (err) {
                console.error('[ExportPanel] List export failed:', err);
                alert(await describeExportError(err, 'List'));
            } finally {
                setLoading(false);
            }
        }
    };

    const exportCount = activeFilterTags.length > 0
        ? 'Exporting filtered classes'
        : 'Exporting all classes';

    return (
        <div className={styles.exportWrapper} ref={panelRef}>
            <button
                className={styles.exportButton}
                onClick={() => setOpen(o => !o)}
                title="Export schedule"
            >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export
            </button>

            {open && (
                <div className={styles.panel}>
                    <p className={styles.panelTitle}>Export schedule</p>

                    <p className={styles.sectionLabel}>Format</p>
                    <div className={styles.formatOptions}>
                        {[
                            { value: 'pdf',  label: 'PDF',  desc: 'Current grid view' },
                            { value: 'list', label: 'List', desc: 'Readable, organized' },
                            { value: 'xls',  label: 'XLS',  desc: 'Spreadsheet (Excel)' },
                        ].map(opt => (
                            <button
                                key={opt.value}
                                className={`${styles.formatBtn} ${format === opt.value ? styles.active : ''}`}
                                onClick={() => setFormat(opt.value)}
                            >
                                <span className={styles.fmtLabel}>{opt.label}</span>
                                <span className={styles.fmtDesc}>{opt.desc}</span>
                            </button>
                        ))}
                    </div>

                    {isWeekView && format === 'pdf' && (
                        <div className={styles.toggleRow} onClick={() => setFitToPage(f => !f)}>
                            <div>
                                <p className={styles.toggleLabel}>Condense</p>
                                <p className={styles.toggleSub}>Fit multiple days to a page</p>
                            </div>
                            <div className={`${styles.toggle} ${fitToPage ? styles.toggleOn : ''}`}>
                                <div className={styles.toggleKnob} />
                            </div>
                        </div>
                    )}

                    {format === 'list' && (
                        <>
                            <p className={styles.sectionLabel}>Group by</p>
                            <select
                                className={styles.select}
                                value={groupBy}
                                onChange={e => setGroupBy(e.target.value)}
                            >
                                {GROUP_BY_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </>
                    )}

                    <div className={styles.filterSection}>
                        <p className={styles.sectionLabel}>
                            Active filters <span className={styles.filterNote}>(from schedule)</span>
                        </p>
                        {activeFilterTags.length > 0 ? (
                            <div className={styles.filterTags}>
                                {activeFilterTags.map((tag, i) => (
                                    <span key={i} className={styles.filterTag}>{tag.label}</span>
                                ))}
                            </div>
                        ) : (
                            <p className={styles.noFilters}>No filters, exporting full schedule</p>
                        )}
                    </div>

                    <button
                        className={styles.exportBtn}
                        onClick={handleExport}
                        disabled={loading}
                    >
                        {loading ? 'Exporting…' : 'Export'}
                    </button>
                    <p className={styles.exportCount}>{exportCount}</p>
                </div>
            )}
        </div>
    );
}
